/**
 * In-app Add-ons management — /app/billing/addons
 * Lists all available add-ons + the ones currently attached to the tenant.
 * Same selectable UX as the public comparison page.
 */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import * as Lucide from "lucide-react";
import { Plus, Minus, Check, X } from "lucide-react";
import StorageMeter from "@/components/StorageMeter";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

async function openRazorpay(checkoutData, onSuccess) {
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Could not load Razorpay. Check your network and retry."); return; }
    const rzp = new window.Razorpay({
        key: checkoutData.key_id,
        amount: checkoutData.amount_paise,
        currency: checkoutData.currency || "INR",
        order_id: checkoutData.order_id,
        name: "Law OS — Add-on",
        description: `${checkoutData.addon.name} (pro-rata for ${checkoutData.pro_rata.days_remaining} days)`,
        theme: { color: "#2D3A8C" },
        handler: () => { if (onSuccess) onSuccess(); },
        modal: { ondismiss: () => toast("Payment cancelled — add-on not attached") },
    });
    rzp.open();
}

export default function AppAddons() {
    const [addons, setAddons] = useState([]);
    const [attached, setAttached] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [a, t] = await Promise.all([
                api.get("/addons").then(r => r.data),
                api.get("/tenant/addons").then(r => r.data),
            ]);
            setAddons(a || []);
            setAttached(t || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { const t = setTimeout(refresh, 0); return () => clearTimeout(t); }, [refresh]);

    const isAttached = (code) => attached.find(t => t.addon_code === code);
    const monthlyTotal = attached.reduce((s, t) => s + (t.monthly_total_inr || 0), 0);

    const attach = async (code, quantity = 1) => {
        setBusy(p => ({ ...p, [code]: true }));
        try {
            const { data } = await api.post("/tenant/addons/checkout", { addon_code: code, quantity });
            // Confirm pro-rata charge with the user
            const ok = window.confirm(
                `Attach "${data.addon.name}"?\n\n` +
                `₹${data.pro_rata.pro_rata_inr.toLocaleString("en-IN")} due today (pro-rata: ${data.pro_rata.days_remaining}/${data.pro_rata.days_in_month} days).\n` +
                `Then ₹${data.pro_rata.full_monthly_inr.toLocaleString("en-IN")}/month from the next cycle.\n\n` +
                `Continue to Razorpay checkout?`
            );
            if (!ok) { setBusy(p => ({ ...p, [code]: false })); return; }
            await openRazorpay(data, () => {
                toast.success("Add-on activating · refreshing…");
                // Webhook may take a moment; poll for a few seconds
                let tries = 0;
                const poll = setInterval(async () => {
                    tries += 1;
                    await refresh();
                    if (tries >= 6) clearInterval(poll);
                }, 2000);
            });
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(p => ({ ...p, [code]: false })); }
    };

    const detach = async (id, name) => {
        if (!window.confirm(`Detach "${name}"? You won't be charged for it next cycle. (Current month is non-refundable.)`)) return;
        setBusy(p => ({ ...p, [id]: true }));
        try {
            await api.delete(`/tenant/addons/${id}`);
            toast.success("Add-on detached");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(p => ({ ...p, [id]: false })); }
    };

    return (
        <div className="space-y-6" data-testid="app-addons-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Add-ons</h1>
                    <p className="text-sm text-muted-foreground mt-1">Extras on top of your plan. Add or remove any time.</p>
                </div>
                {attached.length > 0 && (
                    <div className="bg-primary/10 border border-primary/20 rounded-xl px-4 py-2.5" data-testid="addon-monthly-total">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Active add-ons</div>
                        <div className="text-lg font-heading font-bold text-primary">₹{monthlyTotal.toLocaleString("en-IN")}<span className="text-xs text-muted-foreground font-normal">/mo extra</span></div>
                    </div>
                )}
            </header>

            <StorageMeter/>

            {/* Currently attached */}
            {attached.length > 0 && (
                <section className="bg-card border border-border rounded-2xl shadow-lawos-sm p-6">
                    <h2 className="font-heading text-sm font-semibold mb-4 text-foreground">Active</h2>
                    <div className="divide-y divide-border">
                        {attached.map(t => (
                            <div key={t.id} className="py-3 flex items-center justify-between gap-4" data-testid={`attached-${t.addon_code}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                                        <Check className="w-4 h-4 text-emerald-600"/> {addons.find(a => a.code === t.addon_code)?.name || t.addon_code}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">Qty {t.quantity} · ₹{t.monthly_total_inr}/mo</div>
                                </div>
                                <button onClick={() => detach(t.id, t.addon_code)} disabled={busy[t.id]}
                                    data-testid={`detach-${t.addon_code}`}
                                    className="text-xs text-rose-600 hover:text-rose-700 hover:underline disabled:opacity-50">
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Available catalogue */}
            <section className="bg-card border border-border rounded-2xl shadow-lawos-sm p-6">
                <h2 className="font-heading text-sm font-semibold mb-4 text-foreground">Available add-ons</h2>
                {loading ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {addons.map(a => {
                            const Icon = Lucide[a.icon] || Lucide.Plus;
                            const active = isAttached(a.code);
                            return (
                                <div key={a.code} data-testid={`available-${a.code}`}
                                    className={`rounded-xl border p-4 ${active ? "border-emerald-300 bg-emerald-50/40" : "border-border hover:border-primary/40"}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-emerald-600 text-white" : "bg-primary/10 text-primary"}`}>
                                            <Icon className="w-4 h-4" strokeWidth={1.75}/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-foreground">{a.name}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <div className="text-sm">
                                            <span className="font-heading text-base font-bold text-foreground">₹{a.price_inr_per_month}</span>
                                            <span className="text-xs text-muted-foreground">/mo</span>
                                        </div>
                                        {active ? (
                                            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">Attached</span>
                                        ) : (
                                            <button onClick={() => attach(a.code, 1)} disabled={busy[a.code]}
                                                data-testid={`attach-${a.code}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50">
                                                <Plus className="w-3 h-3" strokeWidth={2.5}/> Add
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <p className="text-[11px] text-muted-foreground italic">
                All add-ons billed monthly via your saved payment method. Detached add-ons stop billing from the next cycle.
                For questions, contact <a href="mailto:support@lawos.in" className="text-primary hover:underline">support@lawos.in</a>.
            </p>
        </div>
    );
}
